import { Link } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaPen } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { buildRequestFieldDefs, initLenderRequest, phaseInfoResult, resetLenderRequest, selectRequestSubTypesByName, storeFieldChanges, submitEditProjectRequest } from "../../../redux/actions/lenderRequestActions";
import EditableRequestField from "../../LenderRequests/RequestFeed/RequestDetailsModal/EditableRequestField";
import { getGoogleAddressFields } from "../../shared/services/SharedService";
import { DelayFor } from "../../shared/utilities/promise-utils";
import { REQUEST_TYPES } from "../../shared/constants/RequestTypes";

const SUMMARY_ANALYSIS_MAX_LENGTH = 2000;

function validate(fieldDef, value) {
  const validationMessage = fieldDef.required && (value === "" || value == null)
    ? "This field is required."
    : fieldDef.validateComplete && !fieldDef.validateComplete(value)
    ? fieldDef.invalidMessage ?? "Please enter a valid value."
    : "";
  
  return validationMessage;
}

const StyledTooltip = withStyles({
  tooltip: {
    color: "black",
    backgroundColor: "white",
    border: "1px solid #E6E8ED",
    fontSize: 14,
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "1px solid #E6E8ED",
    },
  }
})(Tooltip);

export const EditFieldsPage = ({stepCount, incrementCounter, phaseInfo}) => {
  const draftChanges = useSelector((state) => state.lenderRequestReducer.editingChanges);
  const fieldDefsMap = useSelector(state => state.lenderRequestReducer.phaseFieldsMap);  

  const [changes, setChanges] = useState(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Track whether the user has attempted to submit the form. If so, force all validation messages to be visible
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Track which fields should show validation errors
  const [showValidation, setShowValidation] = useState(new Set());  

  // Store the validation message for each field (if any), regardless of whether it should be visible yet
  const [validationErrors, setValidationErrors] = useState(new Map());

  const [summaryAnalysis, setSummaryAnalysis] = useState("");

  // Ref for street address input to attach Google Autocomplete
  const streetInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(resetLenderRequest());
    dispatch(initLenderRequest(REQUEST_TYPES.LENDER_EDIT_PHASE));
    dispatch(buildRequestFieldDefs());
  }, []);

  // Initialize Google Autocomplete for street address field
  useEffect(() => {
    const isStreetEditing = changes.has("street");
    
    if (!isStreetEditing || !streetInputRef?.current) {
      // Cleanup previous autocomplete if street editing stopped
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocompleteConfig = {
        componentRestrictions: { country: 'US' },
        fields: ["address_components", "geometry"],
        types: ['address']
      };

      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, autocompleteConfig);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;

        const address = getGoogleAddressFields(place);
        
        if (address.street) {
          const currentChange = changes.get("street");
          if (currentChange) {
            const newChange = { ...currentChange, newValue: address.street };
            setChanges(prev => new Map(prev.set("street", newChange)));
            
            // Trigger address validation
            handleAddressChange(address);
          }
        }
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Autocomplete:', error);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [changes.has("street"), phaseInfo]);

  useEffect(() => {
    if (draftChanges && fieldDefsMap?.size && phaseInfo) {
      const showValidation = new Set();
      const validationErrors = new Map();
      const changes = new Map();

      draftChanges.forEach(change => {
        const fieldName = change.fieldName;
        const fieldDef = fieldDefsMap.get(fieldName);
        
        if (!fieldDef) {
          console.warn(`Field definition not found for: ${fieldName}`);
          return;
        }

        changes.set(fieldName, { ...change });
        showValidation.add(fieldName);

        const validationMessage = validate(fieldDef, change.newValue);  
        if (validationMessage) {
          validationErrors.set(fieldName, validationMessage);
        }
      });

      setChanges(changes);
      setShowValidation(showValidation);
      setValidationErrors(validationErrors);
    }
  }, [draftChanges, phaseInfo, fieldDefsMap]);

  useEffect(() => {
    if (phaseInfo) {
      const hasChanges = [...changes.values()].some(change => 
        change.newValue !== phaseInfo[change.fieldName]
      );

      setHasChanges(hasChanges);
    }
  }, [changes, phaseInfo]);

  function handleChange(fieldName, modifiedValue, validationMessage) {
    if (!changes.has(fieldName)) {
      console.warn(`No existing change found for field: ${fieldName}`);
      return;
    }
    
    const _change = { 
      ...changes.get(fieldName),
      newValue: modifiedValue
    };
    setChanges(prev => new Map(prev.set(fieldName, _change)));
    
    if (validationMessage) {
      setValidationErrors(prev => new Map(prev).set(fieldName, validationMessage));
    } else {
      setValidationErrors(prev => {
         const newValidationErrors = new Map(prev);
         newValidationErrors.delete(fieldName);
         return newValidationErrors;
      });
    }    
  }

  // Handle address change from Google Autocomplete. Note that this runs asynchronously after the street field is updated.
  function handleAddressChange(address) {
    // Use a short delay to give the other handlers a chance to run
    DelayFor(20).then(() => {
      const isValidLocation = 
        address.city && address.state && address.zip &&
        address.city.toLowerCase() === phaseInfo.city.toLowerCase() && 
        address.state.toLowerCase() === phaseInfo.state.toLowerCase() && 
        address.zip === phaseInfo.zip5;

      const invalidAddressMessage = "The selected address does not match the city, state, or zip of the project.";
      
      if (isValidLocation) {
          setValidationErrors(prev => {
            const newValidationErrors = new Map(prev);
            newValidationErrors.delete("street");
            return newValidationErrors;
          });
        } else if (address.street) {
          console.warn('Selected address does not match project location', {address, phaseInfo});
          setValidationErrors(prev => new Map(prev).set("street", invalidAddressMessage));
        }

      if (!showValidation.has("street")) {
        setShowValidation(prev => new Set(prev).add("street"));
      }
    });
  };

  function startEditing(fieldName) {
    const oldValue = phaseInfo[fieldName];

    setChanges(prev => new Map(prev).set(fieldName, {
      fieldName,
      oldValue: oldValue,
      newValue: oldValue
    }));
  }

  function cancelEditing(fieldName) {
    setChanges(prev => {
      const _changes = new Map(prev);
      _changes.delete(fieldName);
      return _changes;
    });
    
    setValidationErrors(prev => {
      const _validationErrors = new Map(prev);
      _validationErrors.delete(fieldName);
      return _validationErrors;
    });

    setShowValidation(prev => {
      const _showValidation = new Set(prev);
      _showValidation.delete(fieldName);
      return _showValidation;
    });
  }

  function handleBlur(fieldName, validationMessage) {  
    const currentChange = changes.get(fieldName);

    if (currentChange && (fieldName !== "street" || !currentChange.newValue || !validationMessage)) {
      // If there is a validation error, show it immediately when the user blurs the field
      setShowValidation(prev => new Set(prev).add(fieldName));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    // Use a short delay to give the other handlers a chance to run and update validation errors before we check them here.
    DelayFor(30).then(async () => {
      setIsSubmitted(true);

      // Activate all validation messages on submit
      setShowValidation(new Set(changes.keys()));

      if (!hasChanges || validationErrors.size || !summaryAnalysis?.trim()) {
        return;
      }

      // Remove any changes where the current value matches the original value to avoid sending empty changes to the API
      const _changes = new Map(changes);
      _changes.forEach((change, fieldName) => {
        if (change.newValue === phaseInfo[fieldName]) {
          _changes.delete(fieldName);
        }
      });
      setChanges(_changes);
      
      try {
        // Store trimmed summary in phaseInfo for API submission
        phaseInfo.summaryAnalysis = summaryAnalysis.trim();
        dispatch(phaseInfoResult(phaseInfo));
        dispatch(storeFieldChanges([..._changes.values()]));
        await dispatch(selectRequestSubTypesByName(["Update Phase"]));

        const requestCallback = async () => {
          const {requestId} = await dispatch(submitEditProjectRequest(phaseInfo.projectId, phaseInfo.phaseId));
          return {requestId};
        };

        incrementCounter(requestCallback);
      } catch (error) {
        console.error('Error submitting phase edit:', error);
      }
    });
  }

  const getEditableField = (fieldName) => {
    const fieldDef = fieldDefsMap.get(fieldName);

    // by design, will throw an exception if fieldDef is missing
    if (!fieldDef) {
      throw new Error(`Render fields: Missing field definition for ${fieldName}`);
    }
    
    const hasChange = changes.has(fieldName);
    const change = changes.get(fieldName);
    const currentValue = hasChange ? change?.newValue : phaseInfo[fieldName];
    const className = hasChange
      ? fieldDef.type === "text"
        ? "request-field-input"
        : ""
      : "field-value";

    return (
      <div className="edit-project-cell">
        <label>
          {fieldDef.label} 
        </label>
        <StyledTooltip 
          placement="top" 
          disableHoverListener={!fieldDef.readonly}
          disableFocusListener={!fieldDef.readonly} 
          disableTouchListener={!fieldDef.readonly} 
          title="This field is read-only and cannot be edited.">        
          <div className="edit-project-field" onClick={() => {!hasChange && !fieldDef.readonly && startEditing(fieldName)}}>
            <EditableRequestField
              fieldDef={fieldDef}
              fieldValue={currentValue}
              isEditing={hasChange}
              className={className}
              inputRef={fieldName === "street" ? streetInputRef : undefined}
              onChanged={(modifiedValue, validationMessage) => {
                handleChange(fieldName, modifiedValue, validationMessage);
              }}
              onBlur={(validationMessage) => {
                handleBlur(fieldName, validationMessage);
              }}
            />
            <div className="edit-controls" style={{paddingLeft: 5}}>
              {changes.has(fieldName) 
                ? <Link style={{color: "#007bff"}} onClick={() => cancelEditing(fieldName)}>Cancel</Link>
                : <FaPen className={"feed-edit-icon " + (fieldDef.readonly ? "is-disabled" : "")} />}
              </div>
              <div className="validation-error">
                {hasChange && showValidation.has(fieldName) && validationErrors.has(fieldName) 
                  ? validationErrors.get(fieldName) 
                  : ""}
              </div>
          </div>
        </StyledTooltip>
      </div>
    );
  }

  return phaseInfo && fieldDefsMap?.size
    ? (
      <Grid item xs={12}>
        <form id="editProjectForm">
          <Grid item xs={12}>
            <h1 style={{ justifyContent: "center" }}>
              Edit Phase
            </h1>
          </Grid>
          <Grid item xs={12}>
            <h6 style={{ justifyContent: "center" }}>
              Step 1 of {stepCount} - Edit Phase Details
            </h6>
          </Grid>

          <div className="edit-project-table" style={{ marginTop: 30 }}>
            {getEditableField("phaseName")}
            {getEditableField("street")}
            {getEditableField("buildingNumber")}
            {getEditableField("unitNumber")}
            {getEditableField("unitCount")}
            {getEditableField("city")}
            {getEditableField("state")}
            {getEditableField("zip5")}

            <div className="edit-project-cell spacer"></div>
            
            {isSubmitted && !hasChanges
              ? <div className="validation-error">Please make at least one change before continuing.</div>
              : isSubmitted && validationErrors.size
              ? <div className="validation-error">Please ensure all fields are valid before continuing.</div>
              : <></>}

            <div className="comments-container" style={{ marginTop: 20, padding: 0, width: "100%" }}>
              <h5>Summary & Analysis<span style={{ color: "red" }}>*</span></h5>
              <p>Provide a summary and analysis for this request:</p>
              <textarea
                rows="3"
                className="form-control"
                maxLength={SUMMARY_ANALYSIS_MAX_LENGTH}
                placeholder="Enter your summary and analysis here..."
                value={summaryAnalysis}
                onChange={e => setSummaryAnalysis(e.target.value)}
              />
              <div className="comments-footer">
                {isSubmitted && !summaryAnalysis?.trim() && (
                  <div className="validation-error">Summary & Analysis is required.</div>
                )}
                <div className="character-count">
                  {summaryAnalysis.length}/{SUMMARY_ANALYSIS_MAX_LENGTH}
                </div>
              </div>
            </div>
          </div>

          <div className="stepper-controls" style={{margin: "40px 0 5px", flexWrap: "wrap", maxWidth: "unset"}}>
            <div className="edit-project-cell spacer"></div>
            <div className="edit-project-cell" style={{display: "flex", flex: "1", flexDirection: "row", justifyContent: "flex-end"}}>
              <Button
                type="submit"
                className={
                  "savebtn " + ( 
                    (!hasChanges || validationErrors.size || !summaryAnalysis?.trim()) 
                      ? "disabled" 
                      : ""
                )}
                style={{
                  height: "40px",
                  width: "150px",
                  textTransform: "unset",
                  display: "flex",
                  justifyContent: "center",
                  alignSelf: "flex-end",
                }}
                onSubmit={handleSubmit}
                onClick={handleSubmit}
                form="editProjectForm"
              >
                Continue
              </Button>
            </div>
          </div>
        </form>
      </Grid>
  ) 
  : (<></>);
};